import React, { useRef, type FC } from 'react'
import { useIntl } from 'react-intl'

import { useRecoilCallback, useRecoilValue } from 'recoil'

import { FormElement, Select } from 'mailup-web-components'

import { uuid } from 'Helpers/uuid'

import useAsyncSelect2 from 'Hooks/useAsyncSelect/useAsyncSelect'

import { INPUT_MESSAGES_MAX_ITEMS } from 'Constants'

import { changeCriterionCallback } from 'Pages/RecipientsList/recoil/callbacks/filters'
import { criterionOptoutMessageState } from 'Pages/RecipientsList/recoil/state/filters'

import {
  fetchMessageById,
  fetchMessages,
} from 'Pages/RecipientsList/recoil/services/asyncSelectMessage'

interface InputOptoutMessageProps {
  index: number
}

const InputOptoutMessage: FC<InputOptoutMessageProps> = ({ index }) => {
  const intl = useIntl()

  const dirty = useRef<boolean>(false)

  const { current: key } = useRef(uuid())

  const { idMessage } = useRecoilValue(criterionOptoutMessageState(index))

  const { options, loading, handleSearch, hint, hintNoResults } =
    useAsyncSelect2({
      key,
      value: idMessage,
      maxItems: INPUT_MESSAGES_MAX_ITEMS,
      fetchItems: async () => await fetchMessages(''),
      fetchItemsByTerm: fetchMessages,
      fetchItemById: fetchMessageById,
      labels: {
        loading: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusLoading',
        }),
        noResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusNoMatches',
        }),
        moreResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusTooManyItems',
        }),
      },
    })

  const changeCriterion = useRecoilCallback(changeCriterionCallback, [])

  const handleChange = (v: any): void => {
    dirty.current = true

    void changeCriterion('idMessage', v, index)
  }

  const handleExpandCollapse = (c: boolean): void => {
    if (!c) {
      handleSearch('')
    }
  }

  const showFeedback = idMessage === undefined && dirty.current

  return (
    <FormElement
      variant={showFeedback ? 'danger' : undefined}
      feedback={
        showFeedback
          ? intl.formatMessage({ id: 'Load.MailupCommon.Required' })
          : undefined
      }
    >
      <Select
        value={idMessage}
        options={options}
        loading={loading}
        onChange={handleChange}
        onSearch={handleSearch}
        onExpandCollapse={handleExpandCollapse}
        hint={hint}
        hintNoResults={hintNoResults}
        data-mu-test="input-optout-message"
        searchable
        placeholder={intl.formatMessage({
          id: 'Load.RecipientsList.InputOptoutMessagePlaceholder',
        })}
        error={showFeedback}
      />
    </FormElement>
  )
}

export default InputOptoutMessage
